import axios from 'axios'
import { make } from 'vuex-pathify'
import store from '../index'
import { formatDate } from '@/util/formatters'
import { formatToCEP, formatToCNPJ, formatToPhone, formatToCapitalized } from 'brazilian-values'
import { TIPO_DESPESA } from '@/enums/payments'
import { STATUS_CONCLUIDO } from '@/enums/purchase'

const state = {
  data: {},
  file: null,
  loading: false,
  dialog: false,
}

const mutations = make.mutations(state)

const taxValue = (field, fallback = 0) => field?.value ?? field ?? fallback
const taxNumber = (field, fallback = 0) => Number(taxValue(field, fallback)) || 0

const actions = {
  importPurchaseXml: async ({ state }, file) => {
    const currentCompany = store.get('authStore@currentCompany')
    const currentUser = store.get('authStore@currentUser')
    const companyId = currentCompany?.id || currentUser?.companyId

    if (!companyId) {
      throw new Error('Informe a empresa para importar a NFe')
    }

    const query = new URLSearchParams({
      companyId,
      ...(currentCompany?.companyId ? { companySubId: currentCompany.companyId } : {}),
    }).toString()
    const formData = new FormData()
    formData.append('file', file)

    state.loading = true

    return axios.post(
            `${process.env.VUE_APP_API_URL}/purchase/import-nfe-xml?${query}`,
            formData,
    )
      .then(res => res.data)
      .finally(() => {
        state.loading = false
      })
  },

  getRequestFromData: ({ state }) => {
    const request = {
      products: [],
      payments: [],

      userId: store.get('authStore@currentUser').id,
      companyId: store.get('authStore@currentCompany').id,
    }

    return request
  },

  getPurchaseFromData: async ({ state }) => {
    const data = state.data
    const purchase = {
      provider: {},
      shippingCompany: {},
      products: [],
      payments: [],

      userId: store.get('authStore@currentUser').id,
      companyId: store.get('authStore@currentCompany').id,
    }

    // buscar fornecedor
    const providerCnpj = formatToCNPJ(data.nfeProc.NFe.infNFe.emit.CNPJ.value)
    const providers = await store.dispatch('providerStore/getByCnpj', providerCnpj)
    const provider = {}

    if (providers.data.length > 0) {
      provider.id = providers.data[0].id
      provider.addresses = providers.data[0].addresses
    } else {
      provider.addresses = [{
        name: 'Endereço principal',
        sequence: 1,
        cep: formatToCEP(data.nfeProc.NFe.infNFe.emit.enderEmit.CEP.value),
        country: formatToCapitalized(data.nfeProc.NFe.infNFe.emit.enderEmit.xPais.value),
        countryCode: data.nfeProc.NFe.infNFe.emit.enderEmit.cPais?.value,
        state: data.nfeProc.NFe.infNFe.emit.enderEmit.UF.value,
        city: formatToCapitalized(data.nfeProc.NFe.infNFe.emit.enderEmit.xMun.value),
        cityCode: data.nfeProc.NFe.infNFe.emit.enderEmit.cMun.value,
        street: formatToCapitalized(data.nfeProc.NFe.infNFe.emit.enderEmit.xLgr.value),
        district: formatToCapitalized(data.nfeProc.NFe.infNFe.emit.enderEmit.xBairro.value),
        complement: data.nfeProc.NFe.infNFe.emit.enderEmit.xCpl?.value,
        number: parseInt(data.nfeProc.NFe.infNFe.emit.enderEmit.nro.value),
      }]
    }

    provider.name = formatToCapitalized(data.nfeProc.NFe.infNFe.emit.xNome.value)
    provider.fantasy = provider.name.replace('LTDA', '').replace('MEI', '').replace('ME', '').replace('EPP', '').replace('EI', '').replace('EIRELI', '')
    provider.cnpj = providerCnpj
    provider.registry = data.nfeProc.NFe.infNFe.emit.IE.value

    const providerPhone = data.nfeProc.NFe.infNFe.emit.enderEmit.fone?.value
    if (providerPhone) {
      if (providerPhone.length === 13) {
        provider.phone = formatToPhone(providerPhone.slice(2)).replace(/\s/g, '')
      } else if (providerPhone.length === 11) {
        provider.phone = formatToPhone(providerPhone.slice(1)).replace(/\s/g, '')
      } else if (providerPhone.length === 10) {
        provider.phone = formatToPhone(providerPhone).replace(/\s/g, '')
      }
    }

    purchase.provider = provider

    // buscar transportadora
    if (data.nfeProc.NFe.infNFe.transp && data.nfeProc.NFe.infNFe.transp.transporta) {
      const shippingCompanies = await store.dispatch('shippingCompanyStore/getByCnpj', formatToCNPJ(data.nfeProc.NFe.infNFe.transp.transporta.CNPJ.value))
      const shippingCompany = {}

      if (shippingCompanies.data.length > 0) {
        shippingCompany.id = shippingCompanies.data[0].id
      }

      shippingCompany.name = formatToCapitalized(data.nfeProc.NFe.infNFe.transp.transporta.xNome.value)
      shippingCompany.cnpj = formatToCNPJ(data.nfeProc.NFe.infNFe.transp.transporta.CNPJ.value)
      shippingCompany.ie = data.nfeProc.NFe.infNFe.transp.transporta.IE?.value
      shippingCompany.state = data.nfeProc.NFe.infNFe.transp.transporta.UF.value
      shippingCompany.city = formatToCapitalized(data.nfeProc.NFe.infNFe.transp.transporta.xMun.value)

      purchase.shippingCompany = shippingCompany
    } else {
      purchase.shippingCompany = null
    }

    // dados gerais
    purchase.notaFiscal = data.nfeProc.NFe.infNFe.ide.nNF.value
    purchase.notaFiscalKey = data.nfeProc.protNFe.infProt.chNFe.value
    purchase.notaFiscalProtocol = data.nfeProc.protNFe.infProt.nProt.value
    purchase.notaFiscalDate = formatDate(new Date(data.nfeProc.protNFe.infProt.dhRecbto.value).toISOString().substring(0, 10))
    purchase.billingKey = data.nfeProc.NFe.infNFe.cobr ? data.nfeProc.NFe.infNFe.cobr.fat.nFat.value : ''
    purchase.deliveryDate = formatDate(new Date().toISOString().substring(0, 10))
    purchase.status = STATUS_CONCLUIDO
    purchase.valueProducts = data.nfeProc.NFe.infNFe.total.ICMSTot.vProd.value
    purchase.discount = data.nfeProc.NFe.infNFe.total.ICMSTot.vDesc.value
    purchase.addition = 0
    purchase.shippingValue = data.nfeProc.NFe.infNFe.total.ICMSTot.vFrete.value
    purchase.value = data.nfeProc.NFe.infNFe.total.ICMSTot.vNF.value

    let observationNFe = ''
    if (data.nfeProc.NFe.infNFe.infAdic.infAdFisco) {
      observationNFe = data.nfeProc.NFe.infNFe.infAdic.infAdFisco.value
    }
    if (data.nfeProc.NFe.infNFe.infAdic.infAdFisco && data.nfeProc.NFe.infNFe.infAdic.infCpl) {
      observationNFe += ' | ' + data.nfeProc.NFe.infNFe.infAdic.infCpl.value
    } else if (data.nfeProc.NFe.infNFe.infAdic.infCpl) {
      observationNFe += data.nfeProc.NFe.infNFe.infAdic.infCpl.value
    }
    purchase.observation = observationNFe

    const payments = Array.isArray(data.nfeProc.NFe.infNFe.pag.detPag) ? data.nfeProc.NFe.infNFe.pag.detPag : [data.nfeProc.NFe.infNFe.pag.detPag]
    let paymentMethod = null
    switch (payments[0].tPag.value) {
      case '01':
        paymentMethod = await store.dispatch('paymentMethodStore/getByName', 'Dinheiro')
        break

      case '03':
        paymentMethod = await store.dispatch('paymentMethodStore/getByName', 'Cartão de Crédito')
        break

      case '04':
        paymentMethod = await store.dispatch('paymentMethodStore/getByName', 'Cartão de Débito')
        break

      case '15':
        paymentMethod = await store.dispatch('paymentMethodStore/getByName', 'Boleto')
        break

      case '16':
        paymentMethod = await store.dispatch('paymentMethodStore/getByName', 'Depósito')
        break

      case '17':
        paymentMethod = await store.dispatch('paymentMethodStore/getByName', 'PIX')
        break

      default:
        paymentMethod = await store.dispatch('paymentMethodStore/getByName', 'Outros')
        break
    }

    if (paymentMethod != null && paymentMethod.data.length > 0) {
      purchase.paymentMethodId = paymentMethod.data[0].id
    }

    // impostos
    purchase.vBC = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vBC.value)
    purchase.vICMS = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vICMS.value)
    purchase.vFCP = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vFCP.value)
    purchase.vFCPST = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vFCPST.value)
    purchase.vBCST = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vBCST.value)
    purchase.vST = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vST.value)
    purchase.vIPI = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vIPI.value)
    purchase.vPIS = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vPIS.value)
    purchase.vCOFINS = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vCOFINS.value)

    // produtos
    const importProducts = Array.isArray(data.nfeProc.NFe.infNFe.det) ? data.nfeProc.NFe.infNFe.det : [data.nfeProc.NFe.infNFe.det]
    const shippingValue = parseFloat(data.nfeProc.NFe.infNFe.total.ICMSTot.vFrete.value)
    const promises = importProducts.map(async item => {
      const taxes = await store.dispatch('nfeStore/getImposto', item.imposto)
      const products = await store.dispatch('productStore/getByProductCode', { providerCnpj, productCode: item.prod.cProd.value })

      const productId = products.data.length > 0 ? products.data[0].id : undefined
      const productCostPercent = products.data.length > 0 ? products.data[0].costPercent : undefined
      const productProfit = products.data.length > 0 ? products.data[0].profit : undefined
      const unityId = products.data.length > 0 ? products.data[0].unityId : undefined

      const product = {
        productId,
        productCode: item.prod.cProd.value,
        productName: formatToCapitalized(item.prod.xProd.value),
        providerCnpj: providerCnpj,

        unityId,
        uCom: item.prod.uCom.value,
        qCom: item.prod.qCom.value,

        cfop: item.prod.CFOP.value,
        ncm: item.prod.NCM.value,
        cest: item.prod.CEST?.value,

        sequence: parseInt(item.attributes.nItem),
        unit: parseFloat(item.prod.vUnCom.value),
        discount: parseFloat(item.prod.vDesc?.value),
        addition: 0,
        shippingValue: shippingValue > 0 ? shippingValue / importProducts.length : 0,
        total: parseFloat(item.prod.vProd.value),

        costPercent: productCostPercent || 0,
        profit: productProfit || 0,

        observation: item.prod.infAdProd?.value,

        ...taxes,
        taxesNfe: item.imposto,
      }

      const costPercent = Number(product.costPercent) || Number(store.get('authStore@currentCompany').costPercent) || 0
      const profit = Number(product.profit) || Number(store.get('authStore@currentCompany').profit) || 0

      product.tax = product.tax / product.qCom
      product.cost = product.unit + product.shippingValue + product.tax + (product.unit * costPercent / 100)
      product.price = product.cost + (product.cost * profit / 100)

      return product
    })
    purchase.products = await Promise.all(promises)

    // financeiro
    if (data.nfeProc.NFe.infNFe.cobr) {
      const installments = Array.isArray(data.nfeProc.NFe.infNFe.cobr.dup) ? data.nfeProc.NFe.infNFe.cobr.dup : [data.nfeProc.NFe.infNFe.cobr.dup]

      purchase.quotas = installments.length
      purchase.firstDueDate = formatDate(new Date(installments[0].dVenc.value).toISOString().substring(0, 10))
      purchase.payments = installments.map(item => {
        return {
          paymentMethodId: purchase.paymentMethodId,
          type: TIPO_DESPESA,
          description: `Despesa gerada automaticamente a partir da NFe ${purchase.notaFiscal}`,
          sequence: parseInt(item.nDup.value),
          dueDate: formatDate(new Date(item.dVenc.value).toISOString().substring(0, 10)),
          value: parseFloat(item.vDup.value),
          quotas: installments.length,
          numberDocument: purchase.notaFiscal,
          observation: observationNFe,
          payed: false,
        }
      })
    }

    return purchase
  },

  getImposto: (context, imposto) => {
    const res = {}
    let tax = 0

    if ('ICMS' in imposto) {
      if ('ICMS00' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS00.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS00.vICMS)
        res.cst = taxValue(imposto.ICMS.ICMS00.CST)

        tax += Number(res.vICMS)
      } else if ('ICMS10' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS10.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS10.vICMS)
        res.pICMSST = taxValue(imposto.ICMS.ICMS10.pICMSST)
        res.vICMSST = taxValue(imposto.ICMS.ICMS10.vICMSST)
        res.cst = taxValue(imposto.ICMS.ICMS10.CST)

        tax += Number(res.vICMS) + Number(res.vICMSST)
      } else if ('ICMS20' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS20.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS20.vICMS)
        res.cst = taxValue(imposto.ICMS.ICMS20.CST)

        tax += Number(res.vICMS)
      } else if ('ICMS30' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS30.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS30.vICMS)
        res.pICMSST = taxValue(imposto.ICMS.ICMS30.pICMSST)
        res.vICMSST = taxValue(imposto.ICMS.ICMS30.vICMSST)
        res.pFCPST = taxValue(imposto.ICMS.ICMS30.pFCPST)
        res.vFCPST = taxValue(imposto.ICMS.ICMS30.vFCPST)
        res.cst = taxValue(imposto.ICMS.ICMS30.CST)

        tax += Number(res.vICMS) + Number(res.vICMSST) + Number(res.vFCPST)
      } else if ('ICMS40' in imposto.ICMS) {
        res.cst = taxValue(imposto.ICMS.ICMS40.CST)
      } else if ('ICMS41' in imposto.ICMS) {
        res.vICMSSTRet = taxValue(imposto.ICMS.ICMS41.vICMSSTRet)
        res.cst = taxValue(imposto.ICMS.ICMS41.CST)
      } else if ('ICMS50' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS50.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS50.vICMS)
        res.pFCP = taxValue(imposto.ICMS.ICMS50.pFCP)
        res.vFCP = taxValue(imposto.ICMS.ICMS50.vFCP)
        res.cst = taxValue(imposto.ICMS.ICMS50.CST)

        tax += Number(res.vICMS) + Number(res.vFCP)
      } else if ('ICMS51' in imposto.ICMS) {
        res.vICMSSTRet = taxValue(imposto.ICMS.ICMS51.vICMSSTRet)
        res.vFCPSTRet = taxValue(imposto.ICMS.ICMS51.vFCPSTRet)
        res.cst = taxValue(imposto.ICMS.ICMS51.CST)
      } else if ('ICMS60' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS60.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS60.vICMS)
        res.pICMSST = taxValue(imposto.ICMS.ICMS60.pICMSST, taxValue(imposto.ICMS.ICMS60.pST))
        res.vICMSST = taxValue(imposto.ICMS.ICMS60.vICMSST, taxValue(imposto.ICMS.ICMS60.vICMSSTRet))
        res.pFCPST = taxValue(imposto.ICMS.ICMS60.pFCPST)
        res.vFCPST = taxValue(imposto.ICMS.ICMS60.vFCPST)
        res.vBCSTRet = taxValue(imposto.ICMS.ICMS60.vBCSTRet)
        res.vICMSSubstituto = taxValue(imposto.ICMS.ICMS60.vICMSSubstituto)
        res.vICMSSTRet = taxValue(imposto.ICMS.ICMS60.vICMSSTRet)
        res.cst = taxValue(imposto.ICMS.ICMS60.CST)

        tax += taxNumber(imposto.ICMS.ICMS60.vICMS) + taxNumber(imposto.ICMS.ICMS60.vICMSST, taxNumber(imposto.ICMS.ICMS60.vICMSSTRet)) + taxNumber(imposto.ICMS.ICMS60.vFCPST)
      } else if ('ICMS70' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS70.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS70.vICMS)
        res.pICMSST = taxValue(imposto.ICMS.ICMS70.pICMSST)
        res.vICMSST = taxValue(imposto.ICMS.ICMS70.vICMSST)
        res.cst = taxValue(imposto.ICMS.ICMS70.CST)

        tax += Number(res.vICMS) + Number(res.vICMSST)
      } else if ('ICMS90' in imposto.ICMS) {
        res.pICMS = taxValue(imposto.ICMS.ICMS90.pICMS)
        res.vICMS = taxValue(imposto.ICMS.ICMS90.vICMS)
        res.pFCP = taxValue(imposto.ICMS.ICMS90.pFCP)
        res.vFCP = taxValue(imposto.ICMS.ICMS90.vFCP)
        res.cst = taxValue(imposto.ICMS.ICMS90.CST)

        tax += Number(res.vICMS) + Number(res.vFCPST)
      }
    }

    if ('IPI' in imposto && 'IPITrib' in imposto.IPI) {
      res.pIPI = taxValue(imposto.IPI.IPITrib.pIPI)
      res.vIPI = taxValue(imposto.IPI.IPITrib.vIPI)
    } else {
      res.pIPI = 0
      res.vIPI = 0
    }

    tax += Number(res.vIPI)

    if ('PIS' in imposto && 'PISAliq' in imposto.PIS) {
      res.pPIS = taxValue(imposto.PIS.PISAliq.pPIS)
      res.vPIS = taxValue(imposto.PIS.PISAliq.vPIS)
    } else {
      res.pPIS = 0
      res.vPIS = 0
    }

    tax += Number(res.vPIS)

    if ('COFINS' in imposto && 'COFINSAliq' in imposto.COFINS) {
      res.pCOFINS = taxValue(imposto.COFINS.COFINSAliq.pCOFINS)
      res.vCOFINS = taxValue(imposto.COFINS.COFINSAliq.vCOFINS)
    } else {
      res.pCOFINS = 0
      res.vCOFINS = 0
    }

    tax += Number(res.vCOFINS)

    res.tax = tax

    return res
  },
}

const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
